<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import ContactAPI from 'dashboard/api/contacts';
import ContactListsAPI from 'dashboard/api/contactLists';
import * as XLSX from 'xlsx';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  list: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isLoading = ref(false);
const isSearching = ref(false);
const isAddingContacts = ref(false);
const isImporting = ref(false);
const importFileRef = ref(null);
const searchQuery = ref('');
const selectedConversationLabel = ref(null);
const selectedAssignee = ref(null);
const selectedCustomAttribute = ref(null);
const customAttributeValue = ref('');
const lastActivityStartDate = ref('');
const lastActivityEndDate = ref('');
const contacts = ref([]);
const searchResults = ref([]);
const selectedContacts = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const searchCurrentPage = ref(1);
const searchTotalPages = ref(1);
const searchTotalCount = ref(0);

// Import progress state
const importProgress = ref(null);

const labels = computed(() => store.getters['labels/getLabels'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const customAttributes = computed(
  () => store.getters['attributes/getContactAttributes'] || []
);

const conversationLabelOptions = computed(() => [
  {
    value: null,
    label: t('CONTACT_LISTS.CONTACTS_DIALOG.ALL_CONVERSATION_LABELS'),
  },
  ...labels.value.map(label => ({
    value: label.title,
    label: label.title,
  })),
]);

const assigneeOptions = computed(() => [
  { value: null, label: t('CONTACT_LISTS.CONTACTS_DIALOG.ALL_AGENTS') },
  ...agents.value.map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

const customAttributeOptions = computed(() => [
  { value: null, label: t('CONTACT_LISTS.CONTACTS_DIALOG.ALL_ATTRIBUTES') },
  ...customAttributes.value.map(attr => ({
    value: attr.attributeKey,
    label: attr.attributeDisplayName,
    displayType: attr.attributeDisplayType,
    values: attr.attributeValues || [],
  })),
]);

const selectedAttributeConfig = computed(() => {
  if (!selectedCustomAttribute.value) return null;
  return customAttributeOptions.value.find(
    opt => opt.value === selectedCustomAttribute.value
  );
});

const isListAttribute = computed(() => {
  return selectedAttributeConfig.value?.displayType === 'list';
});

const listAttributeOptions = computed(() => {
  if (!isListAttribute.value) return [];
  return (
    selectedAttributeConfig.value?.values?.map(val => ({
      value: val,
      label: val,
    })) || []
  );
});

const dialogTitle = computed(
  () => `${t('CONTACT_LISTS.CONTACTS_DIALOG.TITLE')} - ${props.list?.name}`
);

const allSearchSelected = computed(() => {
  if (searchResults.value.length === 0) return false;
  const availableContacts = searchResults.value.filter(
    c => !isContactInList(c)
  );
  return (
    availableContacts.length > 0 &&
    availableContacts.every(c => selectedContacts.value.includes(c.id))
  );
});

const someSearchSelected = computed(() => {
  if (searchResults.value.length === 0) return false;
  const availableContacts = searchResults.value.filter(
    c => !isContactInList(c)
  );
  return (
    availableContacts.some(c => selectedContacts.value.includes(c.id)) &&
    !allSearchSelected.value
  );
});

const selectedCount = computed(() => selectedContacts.value.length);

const fetchContacts = async (page = 1) => {
  if (!props.list) return;

  isLoading.value = true;
  try {
    const response = await store.dispatch('contactLists/getContacts', {
      listId: props.list.id,
      page,
    });
    contacts.value = response.contacts || [];
    currentPage.value = response.meta?.current_page || 1;
    totalPages.value = response.meta?.total_pages || 1;
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Build filter payload for date range filtering
const buildFilterPayload = () => {
  const filters = [];

  // Custom attribute filter
  if (selectedCustomAttribute.value && customAttributeValue.value) {
    const attributeValue =
      typeof customAttributeValue.value === 'object'
        ? customAttributeValue.value.value || customAttributeValue.value
        : customAttributeValue.value;

    filters.push({
      attribute_key: selectedCustomAttribute.value,
      filter_operator: 'equal_to',
      values: [attributeValue],
      custom_attribute_type: 'contact_attribute',
    });
  }

  // Conversation label filter (filters contacts by their conversations' labels)
  if (selectedConversationLabel.value) {
    filters.push({
      attribute_key: 'conversation_labels',
      filter_operator: 'equal_to',
      values: [selectedConversationLabel.value],
    });
  }

  // Conversation assignee filter (filters contacts by their conversations' assignee)
  if (selectedAssignee.value) {
    filters.push({
      attribute_key: 'conversation_assignee_id',
      filter_operator: 'equal_to',
      values: [selectedAssignee.value],
    });
  }

  // Conversation last activity date range filter (filters contacts by their conversations' dates)
  if (lastActivityStartDate.value) {
    filters.push({
      attribute_key: 'conversation_last_activity_at',
      filter_operator: 'is_greater_than',
      values: [lastActivityStartDate.value],
    });
  }

  if (lastActivityEndDate.value) {
    // Add one day to include the end date fully
    const endDate = new Date(lastActivityEndDate.value);
    endDate.setDate(endDate.getDate() + 1);
    const formattedEndDate = endDate.toISOString().split('T')[0];

    filters.push({
      attribute_key: 'conversation_last_activity_at',
      filter_operator: 'is_less_than',
      values: [formattedEndDate],
    });
  }

  // Add query_operator 'and' to all filters except the last one
  const payload = filters.map((filter, index) => {
    if (index < filters.length - 1) {
      return { ...filter, query_operator: 'and' };
    }
    return filter;
  });

  return payload;
};

// Check if we need to use filter API
const needsFilterAPI = () => {
  return (
    (selectedCustomAttribute.value && customAttributeValue.value) ||
    selectedConversationLabel.value ||
    selectedAssignee.value ||
    lastActivityStartDate.value ||
    lastActivityEndDate.value
  );
};

const searchContacts = async (page = 1) => {
  isSearching.value = true;
  try {
    let data;

    // If we have filters that require the filter API
    if (needsFilterAPI()) {
      const filterPayload = buildFilterPayload();

      // If we also have a text search, add name filter
      if (searchQuery.value) {
        // Add query_operator to the last filter if there are any
        if (filterPayload.length > 0) {
          filterPayload[filterPayload.length - 1].query_operator = 'and';
        }
        filterPayload.push({
          attribute_key: 'name',
          filter_operator: 'contains',
          values: [searchQuery.value],
        });
      }

      const queryPayload = { payload: filterPayload };
      const response = await ContactAPI.filter(page, 'name', queryPayload);
      data = response.data;
    } else {
      // Use regular search
      const response = await ContactAPI.search(
        searchQuery.value || '',
        page,
        'name',
        ''
      );
      data = response.data;
    }

    searchResults.value = data.payload || [];
    searchCurrentPage.value = data.meta?.current_page || 1;
    searchTotalPages.value = data.meta?.total_pages || 1;
    searchTotalCount.value = data.meta?.count || 0;
  } catch (error) {
    searchResults.value = [];
    searchTotalCount.value = 0;
  } finally {
    isSearching.value = false;
  }
};

const toggleContactSelection = contactId => {
  const index = selectedContacts.value.indexOf(contactId);
  if (index === -1) {
    selectedContacts.value.push(contactId);
  } else {
    selectedContacts.value.splice(index, 1);
  }
};

const toggleSelectAllSearch = () => {
  const availableContacts = searchResults.value.filter(
    c => !isContactInList(c)
  );
  if (allSearchSelected.value) {
    // Deselect all
    availableContacts.forEach(c => {
      const index = selectedContacts.value.indexOf(c.id);
      if (index !== -1) {
        selectedContacts.value.splice(index, 1);
      }
    });
  } else {
    // Select all
    availableContacts.forEach(c => {
      if (!selectedContacts.value.includes(c.id)) {
        selectedContacts.value.push(c.id);
      }
    });
  }
};

// Select ALL contacts from the current search/filter (all pages, not just current)
const selectAllFromSearch = async () => {
  isSearching.value = true;
  try {
    let allContacts = [];
    let page = 1;
    let hasMore = true;

    // If we have filters that require the filter API
    if (needsFilterAPI()) {
      const filterPayload = buildFilterPayload();

      // If we also have a text search, add name filter
      if (searchQuery.value) {
        // Add query_operator to the last filter if there are any
        if (filterPayload.length > 0) {
          filterPayload[filterPayload.length - 1].query_operator = 'and';
        }
        filterPayload.push({
          attribute_key: 'name',
          filter_operator: 'contains',
          values: [searchQuery.value],
        });
      }

      const queryPayload = { payload: filterPayload };

      while (hasMore && page <= 50) {
        // Limit to 50 pages
        const response = await ContactAPI.filter(page, 'name', queryPayload);
        const data = response.data;
        const contactsList = data.payload || [];
        allContacts = [...allContacts, ...contactsList];
        // Continue if we got results and there might be more pages
        const totalPagesNum = data.meta?.total_pages;
        hasMore =
          contactsList.length > 0 &&
          (totalPagesNum ? page < totalPagesNum : true);
        page++;
      }
    } else {
      // Regular search
      while (hasMore && page <= 50) {
        const response = await ContactAPI.search(
          searchQuery.value || '',
          page,
          'name',
          ''
        );
        const data = response.data;
        const contactsList = data.payload || [];
        allContacts = [...allContacts, ...contactsList];
        // Continue if we got results and there might be more pages
        const totalPagesNum = data.meta?.total_pages;
        hasMore =
          contactsList.length > 0 &&
          (totalPagesNum ? page < totalPagesNum : true);
        page++;
      }
    }

    // Add all contacts that are not already in the list
    let addedCount = 0;
    allContacts.forEach(c => {
      if (!isContactInList(c) && !selectedContacts.value.includes(c.id)) {
        selectedContacts.value.push(c.id);
        addedCount++;
      }
    });

    useAlert(
      t('CONTACT_LISTS.CONTACTS_DIALOG.SELECTED_ALL_SEARCH', {
        count: addedCount,
      })
    );
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isSearching.value = false;
  }
};

const selectAllFromAttribute = async () => {
  if (!selectedCustomAttribute.value || !customAttributeValue.value) return;

  isSearching.value = true;
  try {
    let allContacts = [];
    let page = 1;
    let hasMore = true;

    // Ensure we pass the string value, not an object
    const attributeValue =
      typeof customAttributeValue.value === 'object'
        ? customAttributeValue.value.value || customAttributeValue.value
        : customAttributeValue.value;

    const queryPayload = {
      payload: [
        {
          attribute_key: selectedCustomAttribute.value,
          filter_operator: 'equal_to',
          values: [attributeValue],
          custom_attribute_type: 'contact_attribute',
        },
      ],
    };

    while (hasMore && page <= 50) {
      // Limit to 50 pages
      const response = await ContactAPI.filter(page, 'name', queryPayload);
      const data = response.data;
      const contactsList = data.payload || [];
      allContacts = [...allContacts, ...contactsList];
      // Continue if we got results and there might be more pages
      const totalPages = data.meta?.total_pages;
      hasMore =
        contactsList.length > 0 && (totalPages ? page < totalPages : true);
      page++;
    }

    // Add all contacts that are not already in the list
    let addedCount = 0;
    allContacts.forEach(c => {
      if (!isContactInList(c) && !selectedContacts.value.includes(c.id)) {
        selectedContacts.value.push(c.id);
        addedCount++;
      }
    });

    useAlert(
      t('CONTACT_LISTS.CONTACTS_DIALOG.SELECTED_FROM_ATTRIBUTE', {
        count: addedCount,
      })
    );
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isSearching.value = false;
  }
};

const addSelectedContacts = async () => {
  if (selectedContacts.value.length === 0) return;

  isAddingContacts.value = true;
  try {
    await store.dispatch('contactLists/addContacts', {
      listId: props.list.id,
      contactIds: selectedContacts.value,
    });
    useAlert(
      t('CONTACT_LISTS.CONTACTS_DIALOG.ADD_MULTIPLE_SUCCESS', {
        count: selectedContacts.value.length,
      })
    );
    selectedContacts.value = [];
    searchQuery.value = '';
    searchResults.value = [];
    await fetchContacts(currentPage.value);
    await store.dispatch('contactLists/get');
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isAddingContacts.value = false;
  }
};

const addContact = async contact => {
  try {
    await store.dispatch('contactLists/addContacts', {
      listId: props.list.id,
      contactIds: [contact.id],
    });
    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.ADD_SUCCESS'));
    await fetchContacts(currentPage.value);
    await store.dispatch('contactLists/get');
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  }
};

const removeContact = async contact => {
  try {
    await store.dispatch('contactLists/removeContacts', {
      listId: props.list.id,
      contactIds: [contact.id],
    });
    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.REMOVE_SUCCESS'));
    await fetchContacts(currentPage.value);
    await store.dispatch('contactLists/get');
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  }
};

const isRemovingAll = ref(false);

const removeAllContacts = async () => {
  if (!props.list?.contacts_count || props.list.contacts_count === 0) return;

  isRemovingAll.value = true;
  try {
    // Fetch all contact IDs from all pages
    let allContactIds = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await store.dispatch('contactLists/getContacts', {
        listId: props.list.id,
        page,
      });
      const contactsList = response.contacts || [];
      allContactIds = [...allContactIds, ...contactsList.map(c => c.id)];
      hasMore = response.meta?.current_page < response.meta?.total_pages;
      page++;
    }

    if (allContactIds.length > 0) {
      await store.dispatch('contactLists/removeContacts', {
        listId: props.list.id,
        contactIds: allContactIds,
      });
      useAlert(
        t('CONTACT_LISTS.CONTACTS_DIALOG.REMOVE_ALL_SUCCESS', {
          count: allContactIds.length,
        })
      );
      await fetchContacts(1);
      await store.dispatch('contactLists/get');
    }
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isRemovingAll.value = false;
  }
};

const goToPage = page => {
  if (page >= 1 && page <= totalPages.value) {
    fetchContacts(page);
  }
};

const goToSearchPage = page => {
  if (page >= 1 && page <= searchTotalPages.value) {
    searchContacts(page);
  }
};

const clearSelection = () => {
  selectedContacts.value = [];
};

watch(searchQuery, () => {
  searchCurrentPage.value = 1;
  searchContacts(1);
});

watch([selectedCustomAttribute, customAttributeValue], () => {
  searchCurrentPage.value = 1;
  searchContacts(1);
});

watch([lastActivityStartDate, lastActivityEndDate], () => {
  searchCurrentPage.value = 1;
  searchContacts(1);
});

watch([selectedConversationLabel, selectedAssignee], () => {
  searchCurrentPage.value = 1;
  searchContacts(1);
});

// Reset custom attribute value when attribute changes
watch(selectedCustomAttribute, () => {
  customAttributeValue.value = '';
});

// Handle import progress updates from ActionCable
const handleImportProgress = data => {
  if (data.contact_list_id !== props.list?.id) return;

  importProgress.value = {
    processed: data.processed,
    total: data.total,
    percentage: data.percentage,
    created: data.created,
    found: data.found,
    errors: data.errors,
    contactsCount: data.contacts_count,
  };

  // Refresh contacts list to show new contacts
  if (data.processed > 0 && data.processed % 100 === 0) {
    fetchContacts(currentPage.value);
  }
};

// Handle import completion from ActionCable
const handleImportComplete = data => {
  if (data.contact_list_id !== props.list?.id) return;

  isImporting.value = false;
  importProgress.value = null;

  useAlert(
    t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_SUCCESS', {
      created: data.created,
      found: data.found,
      errors: data.errors,
      total: data.total,
    })
  );

  // Refresh contacts list
  fetchContacts(currentPage.value);
  store.dispatch('contactLists/get');
};

onMounted(() => {
  dialogRef.value?.open();
  fetchContacts();
  store.dispatch('labels/get');
  store.dispatch('attributes/get');
  store.dispatch('agents/get');
  // Initial search to show contacts
  searchContacts(1);

  // Listen for import progress events
  emitter.on('contact_list.import_progress', handleImportProgress);
  emitter.on('contact_list.import_complete', handleImportComplete);
});

onUnmounted(() => {
  // Remove event listeners
  emitter.off('contact_list.import_progress', handleImportProgress);
  emitter.off('contact_list.import_complete', handleImportComplete);
});

const handleClose = () => {
  dialogRef.value?.close();
  emit('close');
};

const isContactInList = contact => {
  return contacts.value.some(c => c.id === contact.id);
};

const isContactSelected = contactId => {
  return selectedContacts.value.includes(contactId);
};

// Import Excel functionality
const openImportDialog = () => {
  importFileRef.value?.click();
};

const formatPhoneNumber = phone => {
  if (!phone) return null;

  // Remove all non-numeric characters
  let cleaned = String(phone).replace(/\D/g, '');

  // Remove DDI 55 if present to normalize
  if (cleaned.startsWith('55') && cleaned.length > 11) {
    cleaned = cleaned.substring(2);
  }

  // Now we should have DDD + number (10 or 11 digits)
  if (cleaned.length < 10 || cleaned.length > 11) {
    return null; // Invalid number
  }

  // Extract DDD (first 2 digits)
  const ddd = parseInt(cleaned.substring(0, 2), 10);
  let number = cleaned.substring(2);

  // Since November 2016, ALL Brazilian DDDs use 9 digits for mobile numbers (with the "9" prefix).
  if (number.length === 8) {
    number = '9' + number;
  }

  // Final format: +55 + DDD + number
  return `+55${ddd.toString().padStart(2, '0')}${number}`;
};

const handleImportExcel = async event => {
  const file = event.target.files[0];
  if (!file) return;

  isImporting.value = true;

  try {
    const reader = new FileReader();

    reader.onload = async e => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON (column A = name, column B = phone)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Skip header row if present and process data
        const contactsToCreate = [];
        const startRow = jsonData[0]?.[0]
          ?.toString()
          .toLowerCase()
          .includes('nome')
          ? 1
          : 0;

        for (let i = startRow; i < jsonData.length; i++) {
          const row = jsonData[i];
          const name = row[0]?.toString().trim();
          const phone = formatPhoneNumber(row[1]);

          if (name && phone) {
            contactsToCreate.push({ name, phone_number: phone });
          }
        }

        if (contactsToCreate.length === 0) {
          useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_NO_CONTACTS'));
          isImporting.value = false;
          return;
        }

        // Use backend API for import (handles background processing for large imports)
        console.log(
          '[Import] Sending contacts to API:',
          contactsToCreate.length
        );
        const response = await ContactListsAPI.importContacts(
          props.list.id,
          contactsToCreate
        );
        const result = response.data;
        console.log('[Import] API response:', result);

        if (result.status === 'error') {
          useAlert(
            result.message || t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_ERROR')
          );
          isImporting.value = false;
        } else if (result.status === 'processing') {
          // Large import - processing in background
          // Set initial progress state
          importProgress.value = {
            processed: 0,
            total: result.total,
            percentage: 0,
            created: 0,
            found: 0,
            errors: 0,
            contactsCount: 0,
          };
          useAlert(
            t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_BACKGROUND', {
              total: result.total,
            })
          );
          // Keep isImporting true - will be set to false by handleImportComplete
        } else {
          // Small import - completed immediately
          await fetchContacts(currentPage.value);
          await store.dispatch('contactLists/get');

          useAlert(
            t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_SUCCESS', {
              created: result.created,
              found: result.found,
              errors: result.errors,
              total: result.total,
            })
          );
          isImporting.value = false;
        }
      } catch (error) {
        console.error(
          '[Import] Error:',
          error.response?.data || error.message || error
        );
        const errorMsg =
          error.response?.data?.message ||
          t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_ERROR');
        useAlert(errorMsg);
        isImporting.value = false;
      } finally {
        // Reset file input
        if (importFileRef.value) {
          importFileRef.value.value = '';
        }
      }
    };

    reader.onerror = () => {
      useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_ERROR'));
      isImporting.value = false;
    };

    reader.readAsArrayBuffer(file);
  } catch (error) {
    useAlert(t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_ERROR'));
    isImporting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    width="2xl"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <!-- Hidden file input for Excel import -->
      <input
        ref="importFileRef"
        type="file"
        accept=".xlsx,.xls,.csv"
        class="hidden"
        @change="handleImportExcel"
      />

      <!-- Search and filter section -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('CONTACT_LISTS.CONTACTS_DIALOG.SEARCH_LABEL') }}
          </label>
          <Button
            type="button"
            size="xs"
            variant="faded"
            color="blue"
            icon="i-lucide-file-spreadsheet"
            :label="t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_EXCEL')"
            :is-loading="isImporting"
            :disabled="!!importProgress"
            @click.stop.prevent="openImportDialog"
          />
        </div>

        <!-- Import Progress Bar -->
        <div
          v-if="importProgress"
          class="p-4 bg-n-alpha-2 rounded-lg border border-n-weak"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_PROGRESS_TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ importProgress.percentage }}%
            </span>
          </div>
          <div class="w-full bg-n-alpha-3 rounded-full h-2.5 mb-2">
            <div
              class="bg-n-blue-text h-2.5 rounded-full transition-all duration-300"
              :style="{ width: `${importProgress.percentage}%` }"
            />
          </div>
          <div class="flex justify-between text-xs text-n-slate-10">
            <span>{{ importProgress.processed }} / {{ importProgress.total }}</span>
            <span>
              {{
                t('CONTACT_LISTS.CONTACTS_DIALOG.IMPORT_PROGRESS_STATS', {
                  created: importProgress.created,
                  found: importProgress.found,
                  errors: importProgress.errors,
                })
              }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-3">
          <Input
            v-model="searchQuery"
            :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SEARCH_PLACEHOLDER')"
            icon="i-lucide-search"
          />
        </div>

        <!-- Custom Attribute Filter -->
        <div v-if="customAttributes.length > 0" class="grid grid-cols-2 gap-3">
          <ComboBox
            v-model="selectedCustomAttribute"
            :options="customAttributeOptions"
            :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ATTRIBUTE')"
            class="[&>div>button]:bg-n-alpha-black2"
          />

          <div v-if="selectedCustomAttribute" class="flex gap-2">
            <ComboBox
              v-if="isListAttribute"
              v-model="customAttributeValue"
              :options="listAttributeOptions"
              :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_VALUE')"
              class="flex-1 [&>div>button]:bg-n-alpha-black2"
            />
            <Input
              v-else
              v-model="customAttributeValue"
              :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.ATTRIBUTE_VALUE')"
              class="flex-1"
            />
            <Button
              v-if="customAttributeValue"
              type="button"
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-check-check"
              :title="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ALL_ATTRIBUTE')"
              @click.stop.prevent="selectAllFromAttribute"
            />
          </div>
        </div>

        <!-- Conversation Label and Assignee Filters -->
        <div class="grid grid-cols-2 gap-3">
          <ComboBox
            v-model="selectedConversationLabel"
            :options="conversationLabelOptions"
            :placeholder="
              t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_CONVERSATION_LABEL')
            "
            class="[&>div>button]:bg-n-alpha-black2"
          />
          <ComboBox
            v-model="selectedAssignee"
            :options="assigneeOptions"
            :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ASSIGNEE')"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <!-- Date Range Filter for Last Activity -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">
              {{ t('CONTACT_LISTS.CONTACTS_DIALOG.LAST_ACTIVITY_FROM') }}
            </label>
            <Input
              v-model="lastActivityStartDate"
              type="date"
              :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.DATE_PLACEHOLDER')"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">
              {{ t('CONTACT_LISTS.CONTACTS_DIALOG.LAST_ACTIVITY_TO') }}
            </label>
            <Input
              v-model="lastActivityEndDate"
              type="date"
              :placeholder="t('CONTACT_LISTS.CONTACTS_DIALOG.DATE_PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Selection actions bar -->
        <div
          v-if="selectedCount > 0"
          class="flex items-center justify-between p-3 bg-n-alpha-2 rounded-lg"
        >
          <span class="text-sm text-n-slate-11">
            {{
              t('CONTACT_LISTS.CONTACTS_DIALOG.SELECTED_COUNT', {
                count: selectedCount,
              })
            }}
          </span>
          <div class="flex gap-2">
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              :label="t('CONTACT_LISTS.CONTACTS_DIALOG.CLEAR_SELECTION')"
              @click.stop.prevent="clearSelection"
            />
            <Button
              type="button"
              size="xs"
              color="blue"
              icon="i-lucide-plus"
              :label="t('CONTACT_LISTS.CONTACTS_DIALOG.ADD_SELECTED')"
              :is-loading="isAddingContacts"
              @click.stop.prevent="addSelectedContacts"
            />
          </div>
        </div>

        <!-- Search results -->
        <div v-if="isSearching" class="flex items-center justify-center py-4">
          <Spinner size="small" />
        </div>

        <div
          v-else-if="searchResults.length > 0"
          class="border border-n-weak rounded-lg"
        >
          <!-- Select all header -->
          <div
            class="flex items-center justify-between p-3 border-b border-n-weak bg-n-alpha-1"
          >
            <div class="flex items-center gap-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  :checked="allSearchSelected"
                  :indeterminate="someSearchSelected"
                  class="w-4 h-4 rounded border-n-weak text-n-blue-text focus:ring-n-blue-text"
                  @change="toggleSelectAllSearch"
                />
                <span class="text-sm text-n-slate-11">
                  {{ t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ALL_PAGE') }}
                </span>
              </label>
              <span class="text-xs text-n-slate-10">
                ({{ searchTotalCount }}
                {{ t('CONTACT_LISTS.CONTACTS_DIALOG.TOTAL_FOUND') }})
              </span>
            </div>
            <Button
              v-if="searchTotalCount > searchResults.length"
              type="button"
              size="xs"
              variant="link"
              color="blue"
              :label="
                t('CONTACT_LISTS.CONTACTS_DIALOG.SELECT_ALL_TOTAL', {
                  count: searchTotalCount,
                })
              "
              :is-loading="isSearching"
              @click.stop.prevent="selectAllFromSearch"
            />
          </div>

          <div class="max-h-48 overflow-auto">
            <div
              v-for="contact in searchResults"
              :key="contact.id"
              class="flex items-center gap-3 p-3 hover:bg-n-alpha-2 border-b border-n-weak last:border-b-0"
              :class="{ 'bg-n-alpha-1': isContactSelected(contact.id) }"
            >
              <input
                v-if="!isContactInList(contact)"
                type="checkbox"
                :checked="isContactSelected(contact.id)"
                class="w-4 h-4 rounded border-n-weak text-n-blue-text focus:ring-n-blue-text"
                @change="toggleContactSelection(contact.id)"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ contact.name }}
                </p>
                <p class="text-xs text-n-slate-10 truncate">
                  {{ contact.phone_number || contact.email }}
                </p>
              </div>
              <Button
                v-if="
                  !isContactInList(contact) && !isContactSelected(contact.id)
                "
                type="button"
                size="xs"
                variant="faded"
                color="slate"
                icon="i-lucide-plus"
                @click.stop.prevent="addContact(contact)"
              />
              <span
                v-else-if="isContactInList(contact)"
                class="text-xs text-n-slate-10 px-2 py-1 bg-n-alpha-2 rounded"
              >
                {{ t('CONTACT_LISTS.CONTACTS_DIALOG.ALREADY_ADDED') }}
              </span>
            </div>
          </div>

          <!-- Search pagination -->
          <div
            v-if="searchTotalPages > 1"
            class="flex items-center justify-center gap-2 p-3 border-t border-n-weak"
          >
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-left"
              :disabled="searchCurrentPage === 1"
              @click.stop.prevent="goToSearchPage(searchCurrentPage - 1)"
            />
            <span class="text-sm text-n-slate-11">
              {{ searchCurrentPage }} / {{ searchTotalPages }}
            </span>
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-right"
              :disabled="searchCurrentPage === searchTotalPages"
              @click.stop.prevent="goToSearchPage(searchCurrentPage + 1)"
            />
          </div>
        </div>

        <div
          v-else-if="
            !isSearching &&
            (searchQuery ||
              needsFilterAPI() ||
              (selectedCustomAttribute && customAttributeValue))
          "
          class="text-center py-4 text-sm text-n-slate-10"
        >
          {{ t('CONTACT_LISTS.CONTACTS_DIALOG.NO_RESULTS') }}
        </div>
      </div>

      <!-- Divider -->
      <hr class="border-n-weak" />

      <!-- Current contacts in list -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="text-sm font-medium text-n-slate-12 block">
            {{ t('CONTACT_LISTS.CONTACTS_DIALOG.CURRENT_CONTACTS') }}
            ({{
              contacts.length > 0 ? list?.contacts_count || contacts.length : 0
            }})
          </label>
          <Button
            v-if="contacts.length > 0"
            type="button"
            size="xs"
            variant="faded"
            color="ruby"
            icon="i-lucide-trash-2"
            :label="t('CONTACT_LISTS.CONTACTS_DIALOG.REMOVE_ALL')"
            :is-loading="isRemovingAll"
            @click.stop.prevent="removeAllContacts"
          />
        </div>

        <div v-if="isLoading" class="flex items-center justify-center py-8">
          <Spinner />
        </div>

        <div
          v-else-if="contacts.length > 0"
          class="border border-n-weak rounded-lg"
        >
          <div
            v-for="contact in contacts"
            :key="contact.id"
            class="flex items-center justify-between p-3 hover:bg-n-alpha-2 border-b border-n-weak last:border-b-0"
          >
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ contact.name }}
              </p>
              <p class="text-xs text-n-slate-10 truncate">
                {{ contact.phone_number || contact.email }}
              </p>
            </div>
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="ruby"
              icon="i-lucide-trash-2"
              @click.stop.prevent="removeContact(contact)"
            />
          </div>

          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="flex items-center justify-center gap-2 p-3 border-t border-n-weak"
          >
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-left"
              :disabled="currentPage === 1"
              @click.stop.prevent="goToPage(currentPage - 1)"
            />
            <span class="text-sm text-n-slate-11">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <Button
              type="button"
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-right"
              :disabled="currentPage === totalPages"
              @click.stop.prevent="goToPage(currentPage + 1)"
            />
          </div>
        </div>

        <div
          v-else
          class="flex flex-col items-center justify-center py-8 text-center"
        >
          <span class="i-lucide-users text-3xl text-n-slate-10 mb-2" />
          <p class="text-sm text-n-slate-11">
            {{ t('CONTACT_LISTS.CONTACTS_DIALOG.NO_CONTACTS') }}
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('CONTACT_LISTS.FORM.CANCEL')"
          @click.stop.prevent="handleClose"
        />
      </div>
    </template>
  </Dialog>
</template>
